<template>
  <v-container fluid>
    <v-btn
      color="primary"
      variant="text"
      class="mb-3"
      @click="showFilters = !showFilters"
    >
      {{ showFilters ? "Скрыть фильтры" : "Показать фильтры" }}
    </v-btn>

    <v-expand-transition>
      <div v-show="showFilters">
        <v-card class="pa-4 mb-4" max-width="400">
          <v-text-field
            v-model="filters.lastname"
            label="Фамилия"
            variant="underlined"
            clearable
          />

          <v-text-field
            v-model="filters.snils"
            label="СНИЛС"
            variant="underlined"
            clearable
            placeholder="000-000-000 00"
            maxlength="14"
            @update:modelValue="onSnilsInput"
          />

          <v-autocomplete
            v-model="filters.speciality"
            :items="specialties"
            label="Специальность"
            variant="underlined"
            clearable
          />

          <v-autocomplete
            v-model="filters.status"
            :items="statuses"
            label="Статус"
            variant="underlined"
            clearable
          />

          <v-autocomplete
            v-model="filters.group"
            :items="groups"
            label="Группа"
            variant="underlined"
            clearable
          />
        </v-card>
      </div>
    </v-expand-transition>
    <div>
      <v-btn
        prepend-icon="mdi-account-plus"
        variant="text"
        color="green-darken-1"
        @click="openStudent(newPersnalCardUrl)"
      >
        Добавить
      </v-btn>
    </div>

    <!-- DESKTOP: ТАБЛИЦА -->
    <v-data-table-server
      v-if="!isMobile"
      :headers="tableHeaders"
      :items="students"
      :items-length="count"
      :items-per-page="15"
      :items-per-page-options="[15]"
      :row-props="getRowProps"
      @update:page="onPageChange"
    >
      <template #item.name="{ item }">
        <div class="d-flex align-center ga-1">
          <v-btn variant="text" :href="item.profileUrl" target="_blank">
            {{ item.name }}
          </v-btn>

          <v-tooltip v-if="item.old_lastname" location="top">
            <template #activator="{ props }">
              <v-icon v-bind="props" size="18" color="orange-darken-3">
                mdi-information-outline
              </v-icon>
            </template>
            <span>Прежняя фамилия: {{ item.old_lastname }}</span>
          </v-tooltip>
        </div>
      </template>

      <template #item.status="{ item }">
        {{ getStatusTitle(item.status) }}
      </template>

      <template #item.actions="{ item }">
        <v-row dense align="center">
          <v-btn
            icon="mdi-open-in-new"
            variant="text"
            color="green-lighten-2"
            :href="item.personalCardUrl"
            target="_blank"
          />

          <v-menu>
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-format-list-checkbox"
                variant="text"
                color="blue-darken-1"
              />
            </template>
            <v-list>
              <v-list-item
                v-for="(link, i) in item.helpLinks"
                :key="i"
                :href="link.url"
                target="_blank"
              >
                <v-list-item-title>{{ link.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn
            v-if="canDeleteStudent(item)"
            icon="mdi-delete"
            color="red"
            variant="text"
            @click="deleteStudent(item)"
          />
        </v-row>
      </template>
    </v-data-table-server>
    <!-- MOBILE: КАРТОЧКИ -->
    <div v-else>
      <v-card
        v-for="student in students"
        :key="student.personal_id"
        class="mb-3"
        :class="Number(student.status) !== 3 ? 'student-row-warning' : ''"
      >
        <v-card-text>
          <div
            class="text-subtitle-1 font-weight-medium d-flex align-center ga-1"
          >
            {{ student.name }}

            <v-menu
              v-if="student.old_lastname"
              location="bottom"
              :close-on-content-click="true"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props" size="18" color="orange-darken-3">
                  mdi-information-outline
                </v-icon>
              </template>

              <v-card class="pa-3">
                <div class="text-body-2">
                  <strong>Прежняя фамилия:</strong><br />
                  {{ student.old_lastname }}
                </div>
              </v-card>
            </v-menu>
          </div>

          <div class="text-body-2">
            <strong>Статус:</strong> {{ getStatusTitle(student.status) }}
          </div>

          <div class="text-body-2">
            <strong>Группа:</strong> {{ student.group_name }}
          </div>

          <div class="text-body-2" v-if="student.phone">
            <strong>Телефон:</strong> {{ student.phone }}
          </div>

          <v-divider class="my-2" />

          <v-row dense>
            <v-btn
              icon="mdi-open-in-new"
              variant="text"
              @click="openStudent(student.personalCardUrl)"
              color="green-lighten-2"
            />

            <v-menu>
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-format-list-checkbox"
                  variant="text"
                  color="blue-darken-1"
                />
              </template>
              <v-list>
                <v-list-item
                  v-for="(link, i) in student.helpLinks"
                  :key="i"
                  :href="link.url"
                  target="_blank"
                >
                  <v-list-item-title>{{ link.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn
              v-if="canDeleteStudent(student)"
              icon="mdi-delete"
              color="red"
              variant="text"
              @click="deleteStudent(student)"
            />
          </v-row>
        </v-card-text>
      </v-card>

      <v-pagination
        v-model="pageNum"
        :length="Math.ceil(count / 15)"
        @update:modelValue="getStudents"
      />
    </div>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title>Подтверждение удаления</v-card-title>
        <v-card-text>
          Удалить студента <strong>{{ studentToDelete?.name }}</strong
          >?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeDeleteDialog">Отмена</v-btn>
          <v-btn color="red" @click="confirmDeleteStudent">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay
      v-model="processData"
      persistent
      class="align-center justify-center"
    >
      <v-progress-circular color="primary" size="64" indeterminate />
    </v-overlay>
  </v-container>
</template>

<script>
import debounce from "lodash/debounce";

export default {
  data() {
    return {
      headers: [
        { title: "id", value: "userid", sortable: false },
        { title: "ФИО", value: "name" },
        { title: "Статус", value: "status" },
        { title: "Группа", value: "group_name" },
        { title: "Email", value: "email" },
        { title: "Телефон", value: "phone" },
        { title: "Действия", value: "actions", sortable: false },
      ],

      students: [],
      specialties: [],
      groups: [],
      statuses: [],

      filters: {
        lastname: "",
        snils: "",
        speciality: null,
        status: null,
        group: null,
      },
      pageNum: 1,
      count: 0,
      showFilters: false,
      processData: false,
      isAdmin: false,
      deleteDialog: false,
      studentToDelete: null,
      debouncedGetStudents: null,
      newPersnalCardUrl: null,
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.display.smAndDown;
    },
    tableHeaders() {
      const d = this.$vuetify.display || {};
      // На больших экранах показываем всё
      if (d.width >= 1700) {
        return this.headers;
      }

      // На не широких скрываем email
      if (d.width >= 1440) {
        return this.headers.filter((h) => !["email"].includes(h.value));
      }

      // На всех остальных скрываем email и phone
      return this.headers.filter(
        (h) => !["email", "phone", "userid", "status"].includes(h.value)
      );
    },
  },

  created() {
    this.getFilters();
    this.debouncedGetStudents = debounce(() => {
      this.pageNum = 1;
      this.getStudents();
    }, 400);

    this.getStudents();
    this.newPersnalCardUrl = this.$model.wwwroot + "/blocks/manage_groups/#/secretariat/studentCard/";
  },
  beforeUnmount() {
    this.debouncedGetStudents?.cancel();
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.debouncedGetStudents();
      },
    },
  },
  methods: {
    onPageChange(page) {
      this.pageNum = page;
      this.getStudents();
    },

    canDeleteStudent(student) {
      return this.isAdmin && [0, 1, 2].includes(Number(student.status));
    },

    getFilters() {
      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/secretariat/students/getFilters"
        )
        .then((res) => {
          this.specialties = res.data.specialties ?? [];
          this.groups = res.data.groups ?? [];
          this.statuses = res.data.statuses ?? [];
        })
        .catch(console.error);
    },

    getStudents() {
      console.log(this.$model);
      this.processData = true;
      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/secretariat/students/getStudents",
          new URLSearchParams({
            filters: JSON.stringify(this.filters),
            page: this.pageNum - 1,
          })
        )
        .then((res) => {
          this.count = res.data.count || 0;
          this.isAdmin = res.data.isAdmin;
          const list = Object.values(res.data.students ?? {});

          this.students = list.map((s, index) => {
            const helpLinks = [];

            helpLinks.push({
              name: "Личная карточка",
              url:
                this.$model.wwwroot +
                "/blocks/manage_groups/personal/print_personal.php?id=" +
                s.personal_id,
            });

            helpLinks.push({
              name: "Справка в школу",
              url:
                this.$model.wwwroot +
                "/blocks/manage_groups/personal/print_school.php?id=" +
                s.personal_id,
            });

            helpLinks.push({
              name: "Список документов",
              url:
                this.$model.wwwroot +
                "/blocks/manage_groups/personal/print_document_list.php?id=" +
                s.personal_id,
            });

            if ([3, 6].includes(Number(s.status))) {
              helpLinks.push({
                name: "Справка об обучении",
                url:
                  this.$model.wwwroot +
                  "/blocks/manage_groups/personal/print_confirm.php?id=" +
                  s.personal_id,
              });

              helpLinks.push({
                name: "Справка в ПФР",
                url:
                  this.$model.wwwroot +
                  "/blocks/manage_groups/personal/print_pfr.php?id=" +
                  s.personal_id,
              });
            }

            if (s.sex == 1) {
              helpLinks.push({
                name: "Сведения о гражданине для военкомата",
                url:
                  this.$model.wwwroot +
                  "/blocks/manage_groups/personal/print_army_docs.php?id=" +
                  s.personal_id,
              });

              helpLinks.push({
                name: "Карточка гражданина, подлежащего воинскому учету",
                url:
                  this.$model.wwwroot +
                  "/blocks/manage_groups/personal/print_army_docs.php?id=" +
                  s.personal_id +
                  "&action=print_card",
              });

              if (s.status !== 4) {
                helpLinks.push({
                  name: "Справка для военкомата",
                  url:
                    this.$model.wwwroot +
                    "/blocks/manage_groups/personal/print_army.php?id=" +
                    s.personal_id,
                });
              }

              if (s.doc_id) {
                helpLinks.push({
                  name: "Выписка из приказа",
                  url:
                    this.$model.wwwroot +
                    "/blocks/manage_groups/documents/newDocument.php?action=print&id=" +
                    s.doc_id +
                    "&user_id=" +
                    s.personal_id,
                });
              }

              if (s.user_id) {
                helpLinks.push({
                  name: "Обходной лист",
                  url:
                    this.$model.wwwroot +
                    "/blocks/manage_groups/personal/delete_list.php?id=" +
                    s.personal_id,
                });
              }
            }

            return {
              userid: s.user_id != null && s.user_id != 0 ? s.user_id : null,
              ...s,

              profileUrl:
                s.user_id != null && s.user_id != 0
                  ? this.$model.wwwroot + "/user/profile.php?id=" + s.user_id
                  : null,

              personalCardUrl:
                this.$model.wwwroot +
                "/blocks/manage_groups/#/secretariat/studentCard/" +
                s.personal_id,

              helpLinks,
            };
          });
        })
        .finally(() => (this.processData = false));
    },

    openStudent(url) {
      window.open(url, "_blank");
    },

    deleteStudent(student) {
      this.studentToDelete = student;
      this.deleteDialog = true;
    },
    confirmDeleteStudent() {
      if (!this.studentToDelete) return;

      this.processData = true;
      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/secretariat/students/deleteStudent",
          new URLSearchParams({
            id: this.studentToDelete.personal_id,
          })
        )
        .then(() => {
          this.getStudents();
          this.closeDeleteDialog();
        })
        .finally(() => {
          this.processData = false;
        });
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.studentToDelete = null;
    },
    getRowProps({ item }) {
      if (Number(item.status) !== 3) {
        return {
          class: "student-row-warning",
        };
      }
      return {};
    },
    getStatusTitle(status) {
      if (!this.statuses || !this.statuses.length) return "";
      const s = this.statuses.find((x) => Number(x.value) === Number(status));
      return s ? s.title : "";
    },

    onSnilsInput(e) {
      let v = e.target.value.replace(/\D/g, "").slice(0, 11);

      if (v.length > 3) v = v.slice(0, 3) + "-" + v.slice(3);
      if (v.length > 7) v = v.slice(0, 7) + "-" + v.slice(7);
      if (v.length > 11) v = v.slice(0, 11) + " " + v.slice(11);

      this.filters.snils = v;
    },
  },
};
</script>
<style>
.student-row-warning {
  background-color: #fff5e0;
}
</style>
